import { createRoot, Root } from "react-dom/client";
import { createElement, ComponentType } from "react";

/**
 * LiveView hook that mounts a React component and keeps it in sync
 * with server-pushed data.
 *
 * Usage in HEEx:
 *   <div id="health-chart" phx-hook="ReactComponent"
 *        data-component="HealthCheckChart"
 *        data-props={Jason.encode!(@chart_data)} />
 *
 * The hook reads `data-component` to pick which React component to render,
 * and `data-props` for the props (JSON-encoded). On LiveView updates,
 * it re-renders with new props.
 */

type ComponentLoader = () => Promise<{ default: ComponentType<any> }>;

const registry: Record<string, ComponentLoader> = {
  HealthCheckChart: () => import("../components/HealthCheckChart"),
  SystemChart: () => import("../components/SystemChart"),
  ResourceHistoryChart: () => import("../components/ResourceHistoryChart"),
  UptimeChart: () => import("../components/UptimeChart"),
};

export const ReactComponent = {
  root: null as Root | null,
  component: null as ComponentType<any> | null,

  async mounted() {
    const name = this.el.dataset.component;
    if (!name || !registry[name]) {
      console.warn(`ReactComponent hook: unknown component "${name}"`);
      return;
    }

    const mod = await registry[name]();
    this.component = mod.default;
    this.root = createRoot(this.el);
    this._render();

    // Listen for server pushes to update props (element-specific and global)
    this.handleEvent(`update-chart-data:${this.el.id}`, (data: any) => {
      this.el.dataset.props = JSON.stringify(data);
      this._render();
    });
    this.handleEvent("update-chart-data", (data: any) => {
      this.el.dataset.props = JSON.stringify(data);
      this._render();
    });
  },

  updated() {
    this._render();
  },

  _render() {
    if (!this.root || !this.component) return;
    const props = JSON.parse(this.el.dataset.props || "{}");
    this.root.render(createElement(this.component, props));
  },

  destroyed() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  },
} as any;
